<template>
	<AdminContainer selected="manage-logs" :loading="state.loading" :fetched="true" @try-again="fetchData">
		<div class="mx-6 my-6 flex flex-col lg:mx-8">
			<div class="flex items-center">
				<span class="text-neutral-900 me-4 text-xl font-bold">
					{{ $t("logs") }}
				</span>
				<div class="flex-grow" />
				<form class="relative me-4" @submit.prevent="search">
					<input
						v-model="state.searchUser"
						class="bg-white text-neutral-900 border-gray-300 w-56 appearance-none rounded-full border-2 py-2 pe-12 ps-4 text-sm outline-none duration-300 focus:border-primary"
						:placeholder="$t('user')"
					/>
					<Icon
						name="mdi:magnify"
						class="text-neutral-500 absolute bottom-0 end-0 top-0 my-auto me-3 h-6 w-6 cursor-pointer duration-300 hover:text-primary"
						@click="search"
					/>
				</form>
				<form class="relative" @submit.prevent="search">
					<input
						v-model="state.searchText"
						class="bg-white text-neutral-900 border-gray-300 w-56 appearance-none rounded-full border-2 py-2 pe-12 ps-4 text-sm outline-none duration-300 focus:border-primary"
						:placeholder="$t('search')"
					/>
					<Icon
						name="mdi:magnify"
						class="text-neutral-500 absolute bottom-0 end-0 top-0 my-auto me-3 h-6 w-6 cursor-pointer duration-300 hover:text-primary"
						@click="search"
					/>
				</form>
			</div>
			<div class="mt-12 flex flex-col">
				<AdminLogCard v-for="(log, i) in state.logs" :key="i" :data="log" class="mb-8" />
			</div>
			<BasePagination
				:total="state.total"
				:limit="state.limit"
				:current="page"
				:link="$routeUrl.ManageLogsPageUrl"
				class="mb-12 mt-12 self-center"
			/>
		</div>
	</AdminContainer>
</template>

<script setup lang="ts">
import type { Log } from "~/models/log"

const logStore = useLogStore()
const route = useRoute()
const { $i18n, $toastErrors, $routeUrl, $router } = useNuxtApp()

definePageMeta({
	middleware: ["admin-auth"]
})

const state = reactive({
	loading: true,
	modalLoading: false,
	fetched: false,
	logs: [] as Log[],
	total: 0,
	searchText: String(route.query.searchText || ""),
	searchUser: String(route.query.searchUser || ""),
	limit: 15
})

const page = computed(() => Math.max(Number(route.params.page) || 1, 1) || 1)

const qSearchText = computed(() => (route.query.searchText || "") as string)

const qSearchUser = computed(() => (route.query.searchUser || "") as string)

watch(
	() => route.query,
	() => fetchData()
)

const fetchData = async () => {
	state.loading = true
	state.fetched = false
	try {
		const data = await logStore.searchLogs({
			user: state.searchUser,
			search: state.searchText,
			limit: state.limit,
			page: page.value
		})
		state.logs = data.logs
		state.total = data.total
		state.fetched = true
	} catch (e) {
		$toastErrors(e)
	}
	state.loading = false
}

const search = () => {
	if (state.searchText !== qSearchText.value || state.searchUser !== qSearchUser.value || page.value !== 1) {
		state.loading = true
		$router.replace({
			path: $routeUrl.ManageLogsPageUrl(1),
			query: {
				searchText: state.searchText || "",
				searchUser: state.searchUser || ""
			}
		})
	} else {
		fetchData()
	}
}

onMounted(() => {
	fetchData()
})

useHead({
	title:
		$i18n.t("logs") +
		(page.value !== 1 ? " " + $i18n.t("page_n", { page: page.value }) : "") +
		" | " +
		$i18n.t("manager_panel")
})
</script>
